<script>
// Module scope — shared by every instance for the whole visit.
const introState = {seen: false};
</script>

<script setup>
import {useAssistantPanel} from "~/composables/useAssistantPanel";

/**
 * The assistant button in the top bar, plus a one-time nudge pointing at it.
 *
 * Both layouts rendered this button identically; it lives here now so the nudge
 * has one home rather than two that can drift.
 *
 * The nudge exists because the assistant is a headset icon among six others, and
 * nobody goes looking for a feature they don't know shipped. It is shown once and
 * never again — a second showing is nagging, not helping.
 */
const {canUseAssistant} = useAuthService();
const {isOpen, toggle} = useAssistantPanel();
const {$locally} = useNuxtApp();

// Per browser, not per account. A user on a second device seeing it once more is
// a far smaller cost than an endpoint, a column and a migration for a nudge.
const SEEN_KEY = 'assistantIntroSeen';

// introState (module scope, plain <script> above; only ever set on the client) survives the layout
// remounting between admin and public pages even when localStorage is
// unavailable (private mode, blocked site data): the nudge then shows at most
// once per visit instead of on every page.
const hasSeen = () => {
  if (introState.seen) return true;
  try {
    return !!$locally.getItem(SEEN_KEY);
  } catch (e) {
    return false;
  }
};
const markSeen = () => {
  introState.seen = true;
  try {
    $locally.setItem(SEEN_KEY, '1');
  } catch (e) {
    // storage blocked — seenThisVisit still stops it re-appearing this visit
  }
};

const showIntro = ref(false);
let timer = null;
let armed = false;

/**
 * Arm the nudge once the user is known to be eligible.
 *
 * Deliberately not a one-shot check on mount: on a fresh sign-in the layout
 * mounts before the user object lands, so canUseAssistant is still false at that
 * moment and a single check would decide "not eligible" permanently — which is
 * exactly the sign-in case where the nudge matters most.
 */
const arm = () => {
  if (armed || !canUseAssistant.value || hasSeen()) return;
  armed = true;

  // A beat after the user is known: arriving on top of a page still assembling
  // itself reads as an interruption, and gives people something to dismiss
  // before they have looked at what they came for.
  timer = setTimeout(() => {
    if (!isOpen.value && !hasSeen()) showIntro.value = true;
  }, 1500);
};

onMounted(() => {
  arm();
  // Client-only, so $locally is real and no timer is ever started during SSR.
  watch(canUseAssistant, arm);
});

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

const dismiss = () => {
  showIntro.value = false;
  markSeen();
};

// Closed any other way (click outside, Esc) counts as seen too — before, only
// "Got it" saved it, so an outside click brought the nudge back on every page.
watch(showIntro, (open, wasOpen) => {
  if (wasOpen && !open) markSeen();
});

// Found it on their own — so it has done its job, whoever opened it.
watch(isOpen, (open) => {
  if (open) dismiss();
});

const onClick = () => {
  dismiss();
  toggle();
};
</script>

<template>
  <!-- The v-btn is the component ROOT on purpose: Vue applies the parent's scoped
       style attribute to a child's root element, which is how this keeps the top
       bar's .tl-iconbtn sizing and hairline border. Wrapping it in anything puts
       the button out of that scope and it silently loses its border. -->
  <v-btn
      v-if="canUseAssistant"
      :icon="true"
      class="tl-iconbtn mr-2"
      aria-label="Help assistant"
      @click="onClick"
  >
    <v-icon size="20">mdi-face-agent</v-icon>

    <!-- open-on-click is off: the button toggles the panel, and the activator is
         only here so the card is anchored under the icon it is talking about.
         The menu teleports to the overlay container, so clicking inside it can't
         bubble back into the button. -->
    <v-menu
        v-model="showIntro"
        activator="parent"
        :open-on-click="false"
        :close-on-content-click="false"
        location="bottom end"
        offset="8"
    >
      <div class="assistant-intro pa-4">
        <div class="assistant-intro__title mb-1">Meet the assistant</div>
        <p class="assistant-intro__body mb-3">
          Ask how anything in Tap Leagues works and get a straight answer. It's in beta,
          so for now only club admins can see it.
        </p>
        <div class="d-flex justify-end">
          <v-btn size="small" variant="tonal" color="primary" @click="dismiss">Got it</v-btn>
        </div>
      </div>
    </v-menu>
  </v-btn>
</template>

<style scoped>
.assistant-intro {
  max-width: 280px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

.assistant-intro__title {
  font-weight: 600;
  font-size: 0.95rem;
}

.assistant-intro__body {
  font-size: 0.85rem;
  line-height: 1.4;
  opacity: 0.8;
}
</style>
